import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import ImdCommandDialog from "./ImdCommandDialog";
import {
    getDevice, sendCommand, cancelCommand, COMMAND_TYPES, STATUS_LABEL, formatIct, POLL_INTERVAL_MS,
} from "./imdRemoteApi";

// หน้าเครื่อง: state ล่าสุด + งานค้าง (ติ๊กเลือก) + ปุ่มคำสั่ง + ประวัติคำสั่ง
// รหัสระบบ (deviceId / id งาน / ref) ซ่อนไว้ กดดูเมื่อต้องการ
const KIND_LABEL = { REFILL: "เพิ่ม", LOAD_OUT: "บรรจุ", SALE: "ขาย", PAYMENT: "ส่งเงิน" };
const READ_ONLY_TYPES = ["REQUEST_SNAPSHOT", "UPLOAD_LOG", "FORCE_UPDATE"];
const DESTRUCTIVE_TYPES = ["CLEAR_LOCAL_ROUND", "CLOSE_SHIFT", "OPEN_SHIFT"];

function Reveal({ value, label = "แสดงรหัส" }) {
    const [shown, setShown] = useState(false);
    return (
        <span>
            <button type="button" onClick={() => setShown((s) => !s)} style={linkBtn}>
                {shown ? "ซ่อนรหัส" : label}
            </button>
            {shown && <code style={{ marginLeft: "6px", fontSize: "12px" }}>{value}</code>}
        </span>
    );
}

export default function ImdDeviceDetail({ pollMs = POLL_INTERVAL_MS }) {
    const { deviceId } = useParams();
    const [detail, setDetail] = useState(null); // รูป { device, state, commands } ตามที่ server ส่ง
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [selected, setSelected] = useState([]);
    const [pending, setPending] = useState(null); // { type, params? } — คำสั่งที่รอ dialog ยืนยัน
    const [submitting, setSubmitting] = useState(false);
    const [dialogError, setDialogError] = useState("");

    const load = useCallback(async () => {
        try {
            setDetail(await getDevice(deviceId));
            setError("");
        } catch (e) {
            setError(e.status === 404 ? "ไม่พบเครื่องนี้" : (e.message || "โหลดไม่สำเร็จ"));
        }
    }, [deviceId]);

    // poll สถานะคำสั่ง 5 วิขณะเปิดหน้า
    useEffect(() => {
        load();
        const timer = setInterval(load, pollMs);
        return () => clearInterval(timer);
    }, [load, pollMs]);

    const device = detail?.device;
    const state = detail?.state;
    const commands = detail?.commands || [];
    const items = state?.outbox?.items || [];
    const selectedItems = selected.filter((id) => items.some((it) => it.id === id));

    const toggle = (id) => setSelected((prev) => (
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));

    const openDialog = (type, params) => {
        setDialogError("");
        setPending({ type, params });
    };

    const closeDialog = () => {
        setPending(null);
        setDialogError("");
    };

    const submit = async (body) => {
        setSubmitting(true);
        setDialogError("");
        // params มาจากปุ่มที่กด (ลบงานค้างที่เลือก/ทั้งหมด) หรือจาก dialog (เริ่มกะ)
        const payload = { type: pending.type, ...body, ...(pending.params ? { params: pending.params } : {}) };
        try {
            await sendCommand(deviceId, payload);
            setNotice(`ส่ง "${COMMAND_TYPES[pending.type].label}" แล้ว · รอเครื่องดึงคำสั่ง`);
            setSelected([]);
            setPending(null);
            await load();
        } catch (e) {
            setDialogError(e.message || "ส่งคำสั่งไม่สำเร็จ");
        } finally {
            setSubmitting(false);
        }
    };

    const cancel = async (cmd) => {
        setNotice("");
        try {
            await cancelCommand(cmd.id);
            setNotice(`ยกเลิก "${COMMAND_TYPES[cmd.type]?.label || cmd.type}" แล้ว`);
            await load();
        } catch (e) {
            setNotice(`ยกเลิกไม่สำเร็จ: ${e.message}`);
        }
    };

    if (error && !device) {
        return (
            <div style={{ maxWidth: "900px" }}>
                <Link to="/ImdRemoteControl">← กลับรายการเครื่อง</Link>
                <div style={{ color: "#c00", marginTop: "12px" }}>⚠️ {error}</div>
            </div>
        );
    }
    if (!device) return <div>กำลังโหลด...</div>;

    return (
        <div style={{ maxWidth: "1000px" }}>
            <Link to="/ImdRemoteControl">← กลับรายการเครื่อง</Link>
            <h3 style={{ marginTop: "8px" }}>
                {device.label || "ยังไม่ตั้งชื่อ"}{" "}
                <span style={device.online ? badgeOnline : badgeOffline}>{device.online ? "ออนไลน์" : "ออฟไลน์"}</span>
            </h3>
            <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
                <Reveal value={device.deviceId} label="แสดงรหัสเครื่อง" />
            </div>

            {error && <div style={{ color: "#c00", marginBottom: "8px" }}>⚠️ {error}</div>}
            {notice && <div style={noticeBox}>{notice}</div>}

            <section style={card}>
                <b>สภาพล่าสุดของเครื่อง</b>
                <div style={grid}>
                    <div>รุ่นแอพ: vc {device.appVersionCode}</div>
                    <div>เห็นล่าสุด: {formatIct(device.lastSeenAt)}</div>
                    <div>ล็อกอินอยู่: {state ? (state.loggedIn ? "ใช่" : "ไม่") : "—"}</div>
                    <div>
                        รอบปัจจุบัน:{" "}
                        {device.activeRound
                            ? `${device.activeRound.started ? "เริ่มแล้ว" : "ยังไม่เริ่ม"}${device.activeRound.driverName ? ` · ${device.activeRound.driverName}` : ""}`
                            : "—"}
                    </div>
                    <div>งานค้างในคิว: {device.outboxCount}</div>
                    <div>อัปเดตรอติดตั้ง: {state ? (state.updatePending ? "ใช่" : "ไม่") : "—"}</div>
                    <div>Device Owner: {state ? (state.isDeviceOwner ? "ใช่" : "ไม่") : "—"}</div>
                    <div>จอเปิดอยู่: {state ? (state.screenOn ? "ใช่" : "ไม่") : "—"}</div>
                    <div>สภาพนี้ตอน: {state ? formatIct(state.capturedAt) : "—"}</div>
                </div>
            </section>

            <section style={card}>
                <b>สั่งเครื่อง</b>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                    {READ_ONLY_TYPES.map((t) => (
                        <button key={t} onClick={() => openDialog(t)} style={t === "FORCE_UPDATE" ? btnWarn : btnPrimary}>
                            {COMMAND_TYPES[t].label}
                        </button>
                    ))}
                </div>
                <div style={{ fontSize: "12px", color: "#c62828", marginTop: "10px", marginBottom: "4px" }}>คำสั่งทำลายข้อมูล (ต้องพิมพ์ชื่อเครื่องยืนยัน)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    <button onClick={() => openDialog("DELETE_OUTBOX_ITEMS", { itemIds: selectedItems })}
                        disabled={selectedItems.length === 0} style={btnDanger}>
                        ลบงานค้างที่เลือก ({selectedItems.length})
                    </button>
                    <button onClick={() => openDialog("DELETE_OUTBOX_ITEMS", { all: true })}
                        disabled={items.length === 0} style={btnDanger}>
                        ลบงานค้างทั้งหมด
                    </button>
                    {DESTRUCTIVE_TYPES.map((t) => (
                        <button key={t} onClick={() => openDialog(t)} style={btnDanger}>
                            {COMMAND_TYPES[t].label}
                        </button>
                    ))}
                </div>
            </section>

            <section style={card}>
                <b>งานค้างในคิว ({items.length})</b>
                {items.length === 0 ? (
                    <div style={{ color: "#666", marginTop: "8px" }}>ไม่มีงานค้าง</div>
                ) : (
                    <table style={table}>
                        <thead>
                            <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                                <th style={th}></th>
                                <th style={th}>ประเภท</th>
                                <th style={th}>เข้าคิวเมื่อ (ICT)</th>
                                <th style={th}>ลองแล้ว</th>
                                <th style={th}>ข้อผิดพลาดล่าสุด</th>
                                <th style={th}>รหัสงาน</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} style={{ borderBottom: "1px solid #eee" }}>
                                    <td style={td}>
                                        <input type="checkbox" aria-label={`เลือกงาน ${KIND_LABEL[it.kind] || it.kind}`}
                                            checked={selected.includes(it.id)} onChange={() => toggle(it.id)} />
                                    </td>
                                    <td style={td}>{KIND_LABEL[it.kind] || it.kind}</td>
                                    <td style={td}>{formatIct(it.enqueuedAt)}</td>
                                    <td style={td}>{it.attempts} ครั้ง</td>
                                    <td style={td}>{it.lastError || "—"}</td>
                                    <td style={td}><Reveal value={it.ref} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <section style={card}>
                <b>ประวัติคำสั่ง (ล่าสุด 20)</b>
                {commands.length === 0 ? (
                    <div style={{ color: "#666", marginTop: "8px" }}>ยังไม่มีคำสั่ง</div>
                ) : (
                    <table style={table}>
                        <thead>
                            <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                                <th style={th}>เวลาสั่ง (ICT)</th>
                                <th style={th}>คำสั่ง</th>
                                <th style={th}>ผู้สั่ง / เหตุผล</th>
                                <th style={th}>สถานะ</th>
                                <th style={th}>ผลจากเครื่อง</th>
                                <th style={th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {commands.map((cmd) => (
                                <tr key={cmd.id} style={{ borderBottom: "1px solid #eee", verticalAlign: "top" }}>
                                    <td style={td}>{formatIct(cmd.createdAt)}</td>
                                    <td style={td}>{COMMAND_TYPES[cmd.type]?.label || cmd.type}</td>
                                    <td style={td}>
                                        <div>{cmd.createdBy}</div>
                                        <div style={{ color: "#666", fontSize: "12px" }}>{cmd.reason}</div>
                                    </td>
                                    <td style={td}>{STATUS_LABEL[cmd.status] || cmd.status}</td>
                                    <td style={td}>
                                        {/* ข้อความจากเครื่องแสดงตามจริง ไม่แปลง/ไม่สรุปทับ */}
                                        {cmd.result?.detail || "—"}
                                        {cmd.result && (cmd.result.before || cmd.result.after) && (
                                            <details style={{ marginTop: "4px" }}>
                                                <summary style={{ fontSize: "12px", cursor: "pointer" }}>snapshot ก่อน/หลัง</summary>
                                                <pre style={pre}>{JSON.stringify({ before: cmd.result.before, after: cmd.result.after }, null, 2)}</pre>
                                            </details>
                                        )}
                                    </td>
                                    <td style={td}>
                                        {cmd.status === "PENDING" && (
                                            <button onClick={() => cancel(cmd)} style={linkBtn}>ยกเลิก</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <ImdCommandDialog
                open={pending !== null}
                type={pending?.type}
                device={device}
                deleteCount={pending?.params?.itemIds?.length ?? (pending?.params?.all ? items.length : 0)}
                submitting={submitting}
                error={dialogError}
                onClose={closeDialog}
                onSubmit={submit}
            />
        </div>
    );
}

const card = { background: "white", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "14px", marginBottom: "14px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: "6px", marginTop: "8px", fontSize: "14px" };
const table = { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "8px" };
const th = { padding: "6px", borderBottom: "1px solid #ddd" };
const td = { padding: "6px", verticalAlign: "top" };
const pre = { background: "#f7f7f7", padding: "6px", fontSize: "11px", overflowX: "auto", maxHeight: "160px" };
const noticeBox = { background: "#e8f4fd", border: "1px solid #b6dcfa", padding: "8px", borderRadius: "4px", marginBottom: "10px", fontSize: "14px" };
const badgeBase = { padding: "2px 8px", borderRadius: "10px", fontSize: "12px", color: "white", verticalAlign: "middle" };
const badgeOnline = { ...badgeBase, background: "#2e7d32" };
const badgeOffline = { ...badgeBase, background: "#9e9e9e" };
const btnBase = { padding: "8px 14px", border: "none", borderRadius: "4px", cursor: "pointer", color: "white" };
const btnPrimary = { ...btnBase, background: "#007bff" };
const btnWarn = { ...btnBase, background: "#ef6c00" };
const btnDanger = { ...btnBase, background: "#c62828" };
const linkBtn = { background: "none", border: "none", color: "#007bff", cursor: "pointer", padding: 0, fontSize: "13px" };
